import { AnalysisData, AnalysisSettings, OffreData } from '../types/index';

/**
 * Calcule toutes les valeurs de l'offre conseillée à partir du taux de capitalisation.
 *
 * Formule principale : offreConseillee = revenuNet / (tauxCapitalisation / 100)
 * Inverse (saisie directe du prix) : tauxCapitalisation = revenuNet / prix * 100
 *
 * Note sur le ROE : utilise `analysis.apport` (downPayment réel de l'utilisateur),
 * contrairement au backend qui utilise aussi `apport = downPayment`.
 * Le `fondsPropreNecessaire` sert uniquement à calculer le prêt hypothécaire de l'offre.
 */
export function calculerOffreData(
  analysis: AnalysisData,
  settings: AnalysisSettings,
  property: { area: number },
  tauxCapitalisation: number,
): OffreData | null {
  if (tauxCapitalisation <= 0) return null;

  // Offre conseillée : revenu net / taux de capitalisation
  const revenuNet = analysis.loyerAnnuel - analysis.chargesPPEAnnuelles
    - analysis.coutsMaintenance - analysis.impotFoncier;
  const offreConseillee = revenuNet / (tauxCapitalisation / 100);

  // Recalcul des charges sur la base de l'offre conseillée
  const coutsMaintenance = offreConseillee * (settings.maintenanceRate / 100);
  const impotFoncier = offreConseillee * (settings.propertyTaxRate / 100);

  // Financement sur la base de l'offre conseillée
  const fraisAcquisition = offreConseillee * (settings.acquisitionFees / 100);
  const prixTotal = offreConseillee + fraisAcquisition;
  const fondsPropreNecessaire = settings.equityIncludesAcquisition
    ? prixTotal * (settings.equityRate / 100)
    : offreConseillee * (settings.equityRate / 100) + fraisAcquisition;
  // pretHypothecaire basé sur les fonds propres nécessaires calculés (pas sur l'apport réel)
  const pretHypothecaire = prixTotal - fondsPropreNecessaire;
  const chargesInteret = pretHypothecaire * (settings.mortgageRate / 100);
  const amortissement = pretHypothecaire * (settings.amortizationRate / 100);

  // Rendements sur la base de l'offre conseillée
  const rendementBrut = offreConseillee > 0 ? (analysis.loyerAnnuel / offreConseillee) * 100 : 0;
  const rendementNet = offreConseillee > 0
    ? ((analysis.loyerAnnuel - analysis.chargesPPEAnnuelles - coutsMaintenance - impotFoncier) / offreConseillee) * 100
    : 0;
  const cashFlowAnnuel = analysis.loyerAnnuel - analysis.chargesPPEAnnuelles
    - coutsMaintenance - chargesInteret - amortissement - impotFoncier;
  const cashFlowMensuel = cashFlowAnnuel / 12;
  // ROE : cash-flow sur les fonds propres nécessaires pour l'offre conseillée
  // (cohérent avec le prix de l'offre — pas l'apport saisi sur le prix affiché)
  const roe = fondsPropreNecessaire > 0 ? (cashFlowAnnuel / fondsPropreNecessaire) * 100 : 0;
  const prixM2Conseille = property.area > 0 ? offreConseillee / property.area : 0;

  return {
    offreConseillee, prixM2Conseille,
    fraisAcquisition, prixTotal, pretHypothecaire, fondsPropreNecessaire,
    chargesInteret, amortissement, coutsMaintenance, impotFoncier,
    rendementBrut, rendementNet, cashFlowAnnuel, cashFlowMensuel, roe,
  };
}
